import { useCallback } from 'react'

import { useFragment } from '@apollo/client'
import { Text } from '@factorialco/deprecated-design-system'
import { F0Box } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import { TasksValueObjectsTaskStatusEnum } from 'generated/resources/datatable.graphql'
import { ManualTaskContentFragmentDoc } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  PossibleTaskState,
  TaskActionsHook,
  TaskCustomComponent,
  TaskDefinition,
} from 'modules/inbox_tasks/lib/abstraction'
import { useUpdateTaskStatuses } from 'modules/inbox_tasks/lib/updateTaskStatus/updateTaskStatus'

type State = PossibleTaskState

const WorkflowHandlerExecutionTaskContent: TaskCustomComponent<
  'WorkflowsWorkflowHandlerExecution',
  State
> = ({ taskId }) => {
  const { data: taskInfo } = useFragment({
    fragment: ManualTaskContentFragmentDoc,
    fragmentName: 'ManualTaskContent',
    from: {
      __typename: 'TasksInboxTask',
      id: taskId,
    },
    optimistic: false,
  })

  if (!taskInfo || !taskInfo?.content) return null

  return (
    <F0Box display="flex" flexDirection="column" gap="3xl" overflowX="hidden">
      <F0Box display="flex" flexDirection="column" gap="sm">
        <Text.Header3>{i18n.t('common.description')}</Text.Header3>
        <RichTextDisplay content={taskInfo.content} className="text-f1-foreground-secondary" />
      </F0Box>
    </F0Box>
  )
}

const useWorkflowActions: TaskActionsHook<'WorkflowsWorkflowHandlerExecution', State> = ({
  taskId,
}) => {
  const updateTaskStatuses = useUpdateTaskStatuses()

  const handleDropdownAction = useCallback(
    async ({ actionId }: { actionId: string }) => {
      const statuses: Record<string, TasksValueObjectsTaskStatusEnum> = {
        'mark-as-done': 'done',
        'mark-as-todo': 'todo',
        'mark-as-in-progress': 'in_progress',
        'mark-as-discarded': 'discarded',
      }

      const newStatus = statuses[actionId]

      if (!newStatus) return

      await updateTaskStatuses({
        taskIds: [taskId],
        status: newStatus,
      })

      return true
    },
    [updateTaskStatuses, taskId]
  )

  return {
    onClickDropdownAction: handleDropdownAction,
  }
}

export const WorkflowHandlerExecutionTaskDefinition: TaskDefinition<
  'WorkflowsWorkflowHandlerExecution',
  State
> = {
  taskableTypename: 'WorkflowsWorkflowHandlerExecution',
  initialState: {},
  component: WorkflowHandlerExecutionTaskContent,
  useActions: useWorkflowActions,
}
